import posthog from "posthog-js";

import {
  isPostHogAnalyticsActive,
  isPostHogAnalyticsEnabled,
} from "@/lib/analytics/runtime";
import type { AnalyticsEventName } from "@/lib/analytics/events";

export type AnalyticsIdentifyProps = {
  username: string;
  account_type: "free" | "pro";
  signup_method: "username-password" | "oauth";
  created_at?: string | null;
  auth_provider?: string | null;
};

const firedOnceKeys = new Set<string>();

function isReady(): boolean {
  return isPostHogAnalyticsActive() && posthog.__loaded === true;
}

/** Dedupe high-signal events within a browser session (e.g. page views). */
export function captureOnce(
  key: string,
  event: AnalyticsEventName,
  properties?: Record<string, unknown>,
): void {
  if (firedOnceKeys.has(key)) return;
  firedOnceKeys.add(key);
  capture(event, properties);
}

export function capture(
  event: AnalyticsEventName,
  properties?: Record<string, unknown>,
): void {
  if (!isReady()) return;
  posthog.capture(event, properties);
}

export function identify(
  userId: string,
  properties: AnalyticsIdentifyProps,
): void {
  if (!userId || !isPostHogAnalyticsActive() || !posthog.__loaded) return;
  posthog.identify(userId, properties);
}

export function reset(): void {
  if (!isPostHogAnalyticsEnabled()) {
    firedOnceKeys.clear();
    return;
  }
  if (posthog.__loaded) {
    posthog.reset();
  }
  firedOnceKeys.clear();
}

export const analytics = {
  capture,
  captureOnce,
  identify,
  reset,
  isReady,
  isEnabled: isPostHogAnalyticsActive,
};
